import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import {
  PartnerNotification,
  listMyNotifications,
  markMyNotificationsRead,
} from '../../services/partnerPortal';
import { formatDateTime } from '../../utils/partnerFormat';
import { Alert, EmptyState, Spinner } from '../partner-network/ui';

const PartnerNotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<PartnerNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listMyNotifications();
      setNotifications(response.data);

      // Opening the list is what counts as reading them.
      if (response.unread > 0) {
        await markMyNotificationsRead();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner className="h-10 w-10" />
      </div>
    );
  }

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="slide-up">
          <div className="mb-8">
            <h1 className="mb-2 font-display text-2xl font-semibold text-white md:text-3xl">
              Notifications
            </h1>
            <p className="text-sm text-muted">
              Updates on your referrals and commissions.
            </p>
          </div>
        </AnimatedSection>

        {error && (
          <Alert tone="error" className="mb-6">
            {error}
          </Alert>
        )}

        {notifications.length === 0 ? (
          <EmptyState
            title="Nothing yet"
            description="We will let you know here whenever one of your referrals moves forward or a commission is paid."
          />
        ) : (
          <ul className="space-y-3">
            {notifications.map((notification) => {
              const body = (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <p className="font-medium text-white">{notification.title}</p>
                    {!notification.readAt && (
                      <span
                        aria-label="Unread"
                        className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary-400"
                      />
                    )}
                  </div>
                  {notification.body && (
                    <p className="mt-1.5 text-sm text-muted">{notification.body}</p>
                  )}
                  <p className="mt-2 text-xs text-muted/70">
                    {formatDateTime(notification.createdAt)}
                  </p>
                </>
              );

              return (
                <li key={notification.id}>
                  {notification.link ? (
                    <Link
                      to={notification.link}
                      className="block rounded-xl border border-surface-border bg-surface-card p-5 transition-colors hover:border-primary-500/30"
                    >
                      {body}
                    </Link>
                  ) : (
                    <div className="rounded-xl border border-surface-border bg-surface-card p-5">
                      {body}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
};

export default PartnerNotifications;
